<template>
  <div class="relative py-24 overflow-hidden bg-background sm:py-32" id="support">
    <!-- Background Pattern -->
    <div class="absolute inset-0 -z-10">
      <div
        class="absolute left-1/2 top-0 -translate-x-1/2 w-[90rem] h-[90rem] bg-gradient-to-br from-primary/5 to-secondary/5 rounded-full blur-3xl"
        aria-hidden="true" />
    </div>

    <div class="px-6 mx-auto max-w-7xl lg:px-8">
      <div class="grid max-w-6xl grid-cols-1 mx-auto gap-16 lg:grid-cols-2 lg:gap-24">
        <!-- Left Column: Info -->
        <div>
          <!-- Section Badge -->
          <div class="inline-flex items-center px-3 py-1 mb-6 text-sm font-medium rounded-full bg-primary/10 text-primary">
            Contact Us
          </div>

          <!-- Heading -->
          <h2 class="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Get in touch
          </h2>

          <!-- Description -->
          <p class="mt-6 text-lg leading-8 text-muted-foreground">
            We're here to help you every step of the way. Whether you have questions about our services, need assistance with your account, or want to share feedback, our dedicated support team is ready to assist you.
          </p>

          <!-- Contact Details -->
          <dl class="mt-10 space-y-6">
            <!-- Address -->
            <div class="flex items-start gap-4">
              <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10">
                <UIcon name="i-heroicons-building-office-2" class="w-6 h-6 text-primary" />
              </div>
              <div>
                <dt class="text-sm font-semibold text-foreground">Office</dt>
                <dd class="mt-1 text-base text-muted-foreground">
                  Blakstadmarka 26<br />
                  1386 Asker, Norway
                </dd>
              </div>
            </div>

            <!-- Email -->
            <div class="flex items-start gap-4">
              <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10">
                <UIcon name="i-heroicons-envelope" class="w-6 h-6 text-primary" />
              </div>
              <div>
                <dt class="text-sm font-semibold text-foreground">Email</dt>
                <dd class="mt-1 text-base">
                  <a href="mailto:support@viminorge.com" class="text-secondary hover:text-secondary/80 transition-colors duration-200">
                    support@viminorge.com
                  </a>
                </dd>
              </div>
            </div>

            <!-- Response Time -->
            <div class="flex items-start gap-4">
              <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10">
                <UIcon name="i-heroicons-clock" class="w-6 h-6 text-primary" />
              </div>
              <div>
                <dt class="text-sm font-semibold text-foreground">Response Time</dt>
                <dd class="mt-1 text-base text-muted-foreground">
                  We typically respond within 24 hours
                </dd>
              </div>
            </div>
          </dl>

          <!-- Support Features -->
          <div class="grid grid-cols-2 gap-4 mt-12">
            <div class="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border">
              <UIcon name="i-heroicons-chat-bubble-left-right" class="w-5 h-5 text-primary flex-shrink-0" />
              <span class="text-sm font-medium text-foreground">Live Chat</span>
            </div>
            <div class="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border">
              <UIcon name="i-heroicons-document-text" class="w-5 h-5 text-primary flex-shrink-0" />
              <span class="text-sm font-medium text-foreground">Documentation</span>
            </div>
          </div>
        </div>

        <!-- Right Column: Contact Form -->
        <div>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Name Fields -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label for="first-name" class="block mb-2 text-sm font-semibold text-foreground">
                  First name <span class="text-destructive">*</span>
                </label>
                <input
                  v-model="form.firstName"
                  type="text"
                  id="first-name"
                  name="first-name"
                  autocomplete="given-name"
                  required
                  class="w-full px-4 py-3 transition-all duration-200 rounded-xl bg-background text-foreground border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground"
                  placeholder="John" />
              </div>

              <div>
                <label for="last-name" class="block mb-2 text-sm font-semibold text-foreground">
                  Last name <span class="text-destructive">*</span>
                </label>
                <input
                  v-model="form.lastName"
                  type="text"
                  id="last-name"
                  name="last-name"
                  autocomplete="family-name"
                  required
                  class="w-full px-4 py-3 transition-all duration-200 rounded-xl bg-background text-foreground border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground"
                  placeholder="Doe" />
              </div>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block mb-2 text-sm font-semibold text-foreground">
                Email <span class="text-destructive">*</span>
              </label>
              <input
                v-model="form.email"
                type="email"
                id="email"
                name="email"
                autocomplete="email"
                required
                class="w-full px-4 py-3 transition-all duration-200 rounded-xl bg-background text-foreground border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground"
                placeholder="john@example.com" />
            </div>

            <!-- Phone (Optional) -->
            <div>
              <label for="phone-number" class="block mb-2 text-sm font-semibold text-foreground">
                Phone number
              </label>
              <input
                v-model="form.phone"
                type="tel"
                id="phone-number"
                name="phone-number"
                autocomplete="tel"
                class="w-full px-4 py-3 transition-all duration-200 rounded-xl bg-background text-foreground border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground"
                placeholder="+47 123 45 678" />
            </div>

            <!-- Message -->
            <div>
              <label for="message" class="block mb-2 text-sm font-semibold text-foreground">
                Message <span class="text-destructive">*</span>
              </label>
              <textarea
                v-model="form.message"
                id="message"
                name="message"
                rows="5"
                required
                class="w-full px-4 py-3 transition-all duration-200 rounded-xl bg-background text-foreground border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground resize-none"
                placeholder="Tell us how we can help you..." />
            </div>

            <!-- Submit Button -->
            <div class="flex items-center justify-between pt-2">
              <p class="text-xs text-muted-foreground">
                <span class="text-destructive">*</span> Required fields
              </p>
              <button
                type="submit"
                :disabled="isSubmitting"
                class="px-8 py-3 text-base font-semibold transition-all duration-200 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
                <span v-if="!isSubmitting" class="flex items-center gap-2">
                  Send Message
                  <UIcon name="i-heroicons-paper-airplane" class="w-5 h-5" />
                </span>
                <span v-else class="flex items-center gap-2">
                  <UIcon name="i-heroicons-arrow-path" class="w-5 h-5 animate-spin" />
                  Sending...
                </span>
              </button>
            </div>

            <!-- Success Message -->
            <div v-if="showSuccess" class="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div class="flex items-start gap-3">
                <UIcon name="i-heroicons-check-circle" class="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 class="text-sm font-semibold text-green-800 dark:text-green-200">
                    Message sent successfully!
                  </h4>
                  <p class="mt-1 text-sm text-green-700 dark:text-green-300">
                    We'll get back to you as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: ''
});

const isSubmitting = ref(false);
const showSuccess = ref(false);

const handleSubmit = async () => {
  isSubmitting.value = true;

  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1500));

  // Show success message
  showSuccess.value = true;
  isSubmitting.value = false;

  // Reset form
  form.value = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  };

  // Hide success message after 5 seconds
  setTimeout(() => {
    showSuccess.value = false;
  }, 5000);
};
</script>
